import { useState, useCallback, useEffect } from "react";
import axios from '../../../api/apiClient';
import { domain, token } from '../../Security';
import {
  fetchDataVesselFunction, // KEPT: still used by VesselFunctionDefinitionTab
} from '../../Personnel/EmployeesListFld/EmployeesListApi';
import { formatDateLong } from "../../../utils/textUtils";

export const useVessel201 = ({ user, showGeneralMessage }) => {
  const [loading, setLoading] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedDataEffectiveDates, setSelectedDataEffectiveDates] = useState(null);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [filterEffectiveDateFrom, setFilterEffectiveDateFrom] = useState("");
  const [filterEffectiveDateTo, setFilterEffectiveDateTo] = useState("");
  const [isGenerate, setIsGenerate] = useState(false);

  // CLEANED: Removed vessels, areasOfOperation, subAreas, departments, positions
  const [options, setOptions] = useState({
    vesselStatuses: [],
    vesselTypes: [],
    vesselFunctions: [],
  });

  // ── Fetch main table data (filtered) ─────────────────────────────────────
  const fetchData = useCallback(async (filterDateFrom = null, filterDateTo = null) => {
    try {
      setLoading(true);

      const response = await axios.get(`${domain}/api/Vessel201`, {
        params: {
          filterDateFrom: filterDateFrom || undefined,
          filterDateTo: filterDateTo || undefined
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(response.data);
      setFilteredData(response.data);
    } catch (error) {
      showGeneralMessage("Error fetching Vessel201 data:", false);
      console.error("Debug data:", error);
      console.error("Debug error:", error?.message);

    } finally {
      setLoading(false);
      setIsGenerate(false);
    }
  }, [domain, token]);

  const fetchDataByVesselIdEffectiveDate = useCallback(async (vesselId, effectiveDate) => {
    try {
      setRequestLoading(true);

      // Using axios 'params' is the cleaner way to handle optional query strings
      const response = await axios.get(`${domain}/api/Vessel201/get-vessel-details/${vesselId}`, {
        params: {
          effectiveDate: effectiveDate || undefined // Omit from query if null/empty
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching Vessel201 data by ID:", error);

      if (error.response?.status === 404) {
        const specificMessage = error.response.data?.message
          || (typeof error.response.data === 'string' ? error.response.data : null)
          || "Error fetching Vessel201 details.";

        showGeneralMessage(specificMessage, false);
      }

      return null;
    } finally {
      setRequestLoading(false);
    }
  }, [domain, token]);

  const fetchVesselEffectiveDates = useCallback(async (vesselId) => {
    try {
      setRequestLoading(true);

      // Using axios 'params' is the cleaner way to handle optional query strings
      const response = await axios.get(`${domain}/api/Vessel201/get-vessel-effective-dates/${vesselId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (Array.isArray(response.data)) {
        return response.data.map(item => ({
          label: item.effectiveDate ? formatDateLong(item.effectiveDate) : "N/A",
          value: item.effectiveDate
        }));
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching Vessel201 data by ID:", error);
      showGeneralMessage(error?.message ?? "Error fetching vessel effective dates.", false);
      return null;
    } finally {
      setRequestLoading(false);
    }
  }, [domain, token]);


  // KEPT: Still used by VesselSummaryTab Select
  const fetchVesselStatuses = async () => {
    const res = await axios.get(`${domain}/api/VesselStatus`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  };

  // KEPT: Still used by VesselSummaryTab Select
  const fetchVesselTypes = async () => {
    const res = await axios.get(`${domain}/api/VesselType/active`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  };

  // ── Fetch dropdowns ───────────────────────────────────────────────────────
  // CLEANED: Only fetch dropdowns that are still used by Select components
  const fetchDropdowns = useCallback(async () => {
    try {
      setDropdownsLoading(true);

      const [
        vesselStatuses,
        vesselTypes,
        vesselFunctions,
      ] = await Promise.all([
        fetchVesselStatuses(),
        fetchVesselTypes(),
        fetchDataVesselFunction(),
      ]);

      setOptions({
        vesselStatuses: vesselStatuses.map((s) => ({
          value: s.id,
          label: s.description,
        })),
        vesselTypes: vesselTypes.map((t) => ({
          value: t.id,
          label: t.name,
          vesselClass: t.vesselClassDescription || "",
          operationGroup: t.operationGroupCode || "",
        })),
        vesselFunctions: vesselFunctions.map((f) => ({
          value: f.id,
          label: f.code,
          vesselFunctionDescription: f.description,
        })),
      });
    } catch (error) {
      console.error("Dropdown fetch error:", error);
    } finally {
      setDropdownsLoading(false);
    }
  }, []);

  // ── Add ───────────────────────────────────────────────────────────────────
  const handleAdd = async (form, onSuccess) => {
    try {
      setRequestLoading(true);
      const payload = buildPayload(form, true);
      await axios.post(`${domain}/api/Vessel201`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showGeneralMessage("Vessel 201 created successfully!", true);
      await fetchData();
      onSuccess?.();
    } catch (error) {
      console.error("Error creating Vessel 201:", error);
      const msg = error?.response?.data?.message || "Error creating Vessel 201.";
      showGeneralMessage(msg, false);
    } finally {
      setRequestLoading(false);
    }
  };

  // ── Update ────────────────────────────────────────────────────────────────
  const handleUpdate = async (form, recordId, onSuccess) => {
    try {
      setRequestLoading(true);
      const payload = buildPayload(form, false);
      await axios.put(`${domain}/api/Vessel201/${recordId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showGeneralMessage("Vessel 201 updated successfully!", true);
      await fetchData();
      onSuccess?.();
    } catch (error) {
      console.error("Error updating Vessel 201:", error);
      const msg = error?.response?.data?.message || "Error updating Vessel 201.";
      showGeneralMessage(msg, false);
    } finally {
      setRequestLoading(false);
    }
  };

  // ── Filter ───────────────────────────────────────────────────────────
  const handleFilter = useCallback(
    (value) => {
      if (!value) {
        setData(filteredData);
        return;
      }
      const lower = value.toLowerCase();
      const res = filteredData.filter(
        (f) =>
          f?.vesselCode?.toLowerCase().includes(lower) ||
          f?.registeredName?.toLowerCase().includes(lower) ||
          f?.vesselName?.toLowerCase().includes(lower)
      );
      setData(res);
    },
    [filteredData]
  );

  // ── Payload builder ───────────────────────────────────────────────────────
  const buildPayload = (form, isAdd) => ({
    vesselId: form.vesselId ? parseInt(form.vesselId) : null,
    effectiveDate: form.effectiveDate || null,
    vesselStatusId: form.vesselStatusId ? parseInt(form.vesselStatusId) : null,
    areaOfOperationId: form.areaOfOperationId ? parseInt(form.areaOfOperationId) : null,
    subAreaId: form.subAreaId ? parseInt(form.subAreaId) : null,
    vesselTypeId: form.vesselTypeId ? parseInt(form.vesselTypeId) : null,
    vessel201Functions: (form.vessel201Functions || []).map((r) => ({
      id: r.id || 0,
      vesselFunctionId: r.vesselFunctionId ? parseInt(r.vesselFunctionId) : null,
      isActive: r.isActive ?? true,
    })),
    crewDefinitions: (form.crewDefinitions || []).map((r) => ({
      id: r.id || 0,
      departmentId: r.departmentId ? parseInt(r.departmentId) : null,
      positionId: r.positionId ? parseInt(r.positionId) : null,
      allowedCrews: r.allowedCrews ? parseInt(r.allowedCrews) : 0,
    })),
    ...(isAdd && { createdById: parseInt(user?.id) }),
    updatedById: parseInt(user?.id),
  });

  return {
    loading,
    dropdownsLoading,
    requestLoading,
    data,
    options,
    filterEffectiveDateFrom,
    setFilterEffectiveDateFrom,
    filterEffectiveDateTo,
    setFilterEffectiveDateTo,
    setIsGenerate,

    fetchData,
    fetchDataByVesselIdEffectiveDate,
    fetchVesselEffectiveDates,
    fetchDropdowns,
    selectedDataEffectiveDates, setSelectedDataEffectiveDates,
    selectedData, setSelectedData,

    handleAdd,
    handleUpdate,
    handleFilter,
  };
};